import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, Lightbulb, RefreshCw, Sparkles } from 'lucide-react';
import { DjayMascot } from './DjayMascot';
type State = 'idle' | 'correct' | 'wrong';
const QUESTION = {
  prompt: 'Wat is 2/5 + 1/5?',
  options: [{
    id: 'a',
    label: '2/10',
    correct: false,
    hint: 'Bij gelijke noemers blijft de noemer staan.'
  }, {
    id: 'b',
    label: '3/5',
    correct: true,
    hint: ''
  }, {
    id: 'c',
    label: '3/10',
    correct: false,
    hint: 'Tellers tel je op, noemer blijft hetzelfde.'
  }, {
    id: 'd',
    label: '1/5',
    correct: false,
    hint: 'Je telt op, dus het wordt méér dan 2/5.'
  }]
};
export function TryItSection() {
  const [state, setState] = useState<State>('idle');
  const [picked, setPicked] = useState<string | null>(null);
  function reset() {
    setState('idle');
    setPicked(null);
  }
  function handlePick(id: string) {
    const opt = QUESTION.options.find(o => o.id === id);
    setPicked(id);
    setState(opt?.correct ? 'correct' : 'wrong');
  }
  const pickedOption = QUESTION.options.find(o => o.id === picked);
  return <section id="probeer" className="relative py-24 lg:py-32 bg-[#FFF9F2] overflow-hidden" data-id="element-738">
      {/* Decorative orbs */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-amber-300/30 rounded-full blur-[100px] pointer-events-none" data-id="element-739" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-rose-300/20 rounded-full blur-[110px] pointer-events-none" data-id="element-740" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-741">
        <div className="text-center mb-12" data-id="element-742">
          <motion.div initial={{
          opacity: 0,
          y: 10,
          rotate: -3
        }} whileInView={{
          opacity: 1,
          y: 0,
          rotate: -2
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6 shadow-lg shadow-orange-500/30" data-id="element-743">
            <Sparkles className="w-3.5 h-3.5" data-id="element-744" />
            Live · niet zomaar een screenshot
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-5 tracking-tight" data-id="element-745">
            Probeer{' '}
            <span className="relative inline-block" data-id="element-746">
              <span className="relative z-10" data-id="element-747">djay zelf</span>
              <span className="absolute inset-x-0 -bottom-1 h-3 bg-amber-300/60 -z-0" data-id="element-748" />
            </span>{' '}
            uit.
          </h2>
          <p className="text-lg text-slate-600 font-medium max-w-2xl mx-auto" data-id="element-749">
            Geen video, geen marketing. Klik gewoon een antwoord. Zie wat een
            kind ziet — inclusief de uitleg als je 't fout hebt.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_auto_1.2fr] gap-8 items-center max-w-5xl mx-auto" data-id="element-750">
          {/* Mascot + speech */}
          <div className="hidden md:flex flex-col items-center text-center" data-id="element-751">
            <DjayMascot size={140} mood={state === 'correct' ? 'celebrating' : state === 'wrong' ? 'encouraging' : 'pointing'} data-id="element-752" />
            <motion.div key={state} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-2 shadow-sm max-w-[200px]" data-id="element-753">
              <div className="text-xs font-black text-slate-700 leading-snug" data-id="element-754">
                {state === 'idle' && 'Welke breuk past hier? Klik een antwoord en ik leg uit waarom! 👇'}
                {state === 'correct' && 'Top! Je hebt door dat bij gelijke noemers alleen de teller verandert. 🎉'}
                {state === 'wrong' && 'Bijna! Lees mijn uitleg en probeer het nog eens. 💪'}
              </div>
            </motion.div>
          </div>

          {/* Vertical separator (desktop only) */}
          <div className="hidden md:block w-px h-64 bg-gradient-to-b from-transparent via-slate-200 to-transparent" data-id="element-755" />

          {/* Question card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-white rounded-3xl border-4 border-white shadow-2xl overflow-hidden" data-id="element-756">
            {/* Mini browser chrome */}
            <div className="h-9 bg-slate-50 border-b border-slate-100 flex items-center px-4 gap-2" data-id="element-757">
              <div className="flex gap-1.5" data-id="element-758">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-400" data-id="element-759" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-760" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-761" />
              </div>
              <span className="flex-1 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-762">
                Breuken · niveau 6
              </span>
              <button onClick={reset} disabled={state === 'idle'} className="text-[10px] font-bold text-slate-400 hover:text-slate-700 disabled:opacity-0 flex items-center gap-1" data-id="element-763">
                <RefreshCw className="w-3 h-3" data-id="element-764" /> Reset
              </button>
            </div>

            <div className="p-6 md:p-8" data-id="element-765">
              <div className="text-[10px] font-black text-orange-600 uppercase tracking-widest mb-3" data-id="element-766">
                Rekenen · Vraag
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-6 leading-tight" data-id="element-767">
                {QUESTION.prompt}
              </h3>

              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-768">
                {QUESTION.options.map(opt => {
                const isPicked = picked === opt.id;
                const showAsCorrect = state !== 'idle' && opt.correct;
                const showAsWrong = isPicked && !opt.correct;
                return <motion.button key={opt.id} onClick={() => state === 'idle' && handlePick(opt.id)} whileHover={state === 'idle' ? {
                  y: -2
                } : undefined} whileTap={state === 'idle' ? {
                  scale: 0.97
                } : undefined} disabled={state !== 'idle'} className={`relative flex items-center justify-between rounded-2xl px-4 py-3.5 border-2 font-black text-base transition-all ${showAsCorrect ? 'bg-emerald-500 border-emerald-600 text-white shadow-lg shadow-emerald-500/30' : showAsWrong ? 'bg-rose-50 border-rose-300 text-rose-700' : state === 'idle' ? 'bg-white border-slate-200 text-slate-700 hover:border-orange-400 hover:bg-orange-50' : 'bg-white border-slate-200 text-slate-400'}`} data-id="element-769">
                      <span className="flex items-center gap-2.5" data-id="element-770">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${showAsCorrect ? 'bg-white/20 text-white' : showAsWrong ? 'bg-rose-200 text-rose-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-771">
                          {opt.id.toUpperCase()}
                        </span>
                        {opt.label}
                      </span>
                      {showAsCorrect && <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} data-id="element-772" />}
                      {showAsWrong && <XCircle className="w-5 h-5" strokeWidth={2.5} data-id="element-773" />}
                    </motion.button>;
              })}
              </div>

              {/* Feedback */}
              <AnimatePresence data-id="element-774">
                {state !== 'idle' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-775">
                    <div className={`rounded-2xl p-4 border-2 ${state === 'correct' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-776">
                      <div className="flex items-start gap-3" data-id="element-777">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${state === 'correct' ? 'bg-emerald-200' : 'bg-rose-200'}`} data-id="element-778">
                          {state === 'correct' ? <Sparkles className="w-4 h-4 text-emerald-700" strokeWidth={3} data-id="element-779" /> : <Lightbulb className="w-4 h-4 text-rose-700" strokeWidth={3} data-id="element-780" />}
                        </div>
                        <div className="flex-1" data-id="element-781">
                          <div className={`text-xs font-black uppercase tracking-widest mb-1 ${state === 'correct' ? 'text-emerald-700' : 'text-rose-700'}`} data-id="element-782">
                            {state === 'correct' ? 'Goed gedaan!' : 'Hier maken kinderen vaak een foutje'}
                          </div>
                          <p className={`text-sm font-bold leading-snug ${state === 'correct' ? 'text-emerald-900' : 'text-rose-900'}`} data-id="element-783">
                            {state === 'correct' ? 'Bij 2/5 + 1/5 blijven de noemers gelijk. Je telt alleen de tellers op: 2 + 1 = 3. Dus 3/5.' : pickedOption?.hint}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-3" data-id="element-784">
                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest" data-id="element-785">
                          <Sparkles className="w-3 h-3" data-id="element-786" />
                          Adaptief vervolg klaar
                        </div>
                        <button onClick={reset} className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-xl px-3 py-2 text-xs font-black hover:bg-slate-800 transition-colors" data-id="element-787">
                          Probeer opnieuw
                          <ArrowRight className="w-3.5 h-3.5" data-id="element-788" />
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* Below the demo: contextualize */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto" data-id="element-789">
          {[{
          n: '1',
          title: 'Geen multiple-choice gegok',
          desc: 'Drag-and-drop, getallenlijnen, klokken, kaarten — elke vraag krijgt de juiste vorm.'
        }, {
          n: '2',
          title: 'Fout? Geen straf.',
          desc: 'djay legt uit wat er misging en kiest meteen een passende vervolgvraag.'
        }, {
          n: '3',
          title: 'Niveau dat meegroeit',
          desc: '16 niveaus per leerdoel. Je kind oefent altijd net op de grens van wat al kan.'
        }].map(b => <div key={b.n} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-id="element-790">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-orange-100 text-orange-700 font-black text-sm mb-3" data-id="element-791">
                {b.n}
              </div>
              <h4 className="font-black text-slate-900 text-sm mb-1.5" data-id="element-792">
                {b.title}
              </h4>
              <p className="text-xs text-slate-600 font-medium leading-relaxed" data-id="element-793">
                {b.desc}
              </p>
            </div>)}
        </motion.div>
      </div>
    </section>;
}